// src/components/shell/Sidebar.tsx

import { useState } from 'react';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Separator } from '@/components/ui/separator';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Sun, Moon, ChevronsUpDown, Check, ChevronLeft, ChevronRight, FolderPlus, Plus, Maximize, Minimize } from 'lucide-react';
import type { AppId, NavItem as NavItemType } from './BottomNav';
import type { Project } from '@/types/projects';
import { NavItem } from './NavItem';
import { useFullscreen } from '@/hooks/useFullscreen';

interface SidebarProps {
  items: NavItemType[];
  activeApp: AppId;
  collapsed: boolean;
  onSelect: (app: AppId) => void;
  onToggleCollapse: () => void;
  projects: Project[];
  activeProject: Project | null;
  onSelectProject: (project: Project) => void;
  onCreateProject: (name: string) => void;
  userName: string;
  userEmail: string;
  onLogout: () => void;
  theme: 'dark' | 'light';
  onThemeToggle: () => void;
  // Sub-nav
  activeSubNav: Record<string, string>;
  onSelectSubNav: (appId: string, subId: string) => void;
}

export function Sidebar({
  items,
  activeApp,
  collapsed,
  onSelect,
  onToggleCollapse,
  projects,
  activeProject,
  onSelectProject,
  onCreateProject,
  userName,
  userEmail,
  onLogout,
  theme,
  onThemeToggle,
  activeSubNav,
  onSelectSubNav,
}: SidebarProps) {
  const [edgeHovered, setEdgeHovered]       = useState(false);
  const [dialogOpen, setDialogOpen]         = useState(false);
  const [newProjectName, setNewProjectName] = useState('');

  const { isFullscreen, toggle: toggleFullscreen } = useFullscreen();

  const initials = userName
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  const handleCreate = () => {
    if (!newProjectName.trim()) return;
    onCreateProject(newProjectName.trim());
    setNewProjectName('');
    setDialogOpen(false);
  };

  return (
    <TooltipProvider delayDuration={200}>
      <div className="relative hidden md:flex flex-shrink-0">
        <aside
          className={cn(
            'flex flex-col h-screen border-r border-border bg-background transition-all duration-200',
            collapsed ? 'w-[52px]' : 'w-[300px]'
          )}
        >
          {/* Project switcher */}
          <div className="flex items-center px-3 py-3 border-b border-border min-h-[52px]">
            {!collapsed && (
              <>
                {projects.length === 0 ? (
                  <button
                    onClick={() => setDialogOpen(true)}
                    className="flex items-center gap-2 min-w-0 flex-1 rounded-md px-1 py-1 hover:bg-accent transition-colors text-muted-foreground"
                  >
                    <div className="w-6 h-6 rounded-md border border-dashed border-border flex items-center justify-center flex-shrink-0">
                      <Plus size={12} />
                    </div>
                    <span className="text-sm truncate">No projects yet</span>
                  </button>
                ) : (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button className="flex items-center gap-2 min-w-0 flex-1 rounded-md px-1 py-1 hover:bg-accent transition-colors">
                        <div className="w-6 h-6 rounded-md bg-primary flex items-center justify-center text-[10px] font-semibold text-primary-foreground flex-shrink-0">
                          {activeProject?.name[0].toUpperCase() ?? 'P'}
                        </div>
                        <span className="text-sm font-medium truncate flex-1 text-left">
                          {activeProject?.name ?? 'Select project'}
                        </span>
                        <ChevronsUpDown size={16} className="text-muted-foreground flex-shrink-0" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent side="bottom" align="start" className="w-[240px]">
                      <DropdownMenuLabel className="text-xs text-muted-foreground">Projects</DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      {projects.map((p) => (
                        <DropdownMenuItem
                          key={p.id}
                          onClick={() => onSelectProject(p)}
                          className="flex items-center justify-between"
                        >
                          <span>{p.name}</span>
                          {p.id === activeProject?.id && <Check size={12} className="text-muted-foreground" />}
                        </DropdownMenuItem>
                      ))}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() => setDialogOpen(true)}
                        className="flex items-center gap-2 text-muted-foreground"
                      >
                        <FolderPlus size={13} />
                        <span>Create project</span>
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </>
            )}
            {collapsed && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={onToggleCollapse}
                    className="w-6 h-6 rounded-md bg-primary flex items-center justify-center text-[10px] font-semibold text-primary-foreground mx-auto"
                  >
                    {activeProject?.name[0].toUpperCase() ?? 'P'}
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right">{activeProject?.name ?? 'No project'}</TooltipContent>
              </Tooltip>
            )}
          </div>

          {/* Nav items */}
          <div className="flex-1 flex flex-col gap-1 px-2 py-3 overflow-y-auto">
            {!collapsed && (
              <span className="text-[10px] text-muted-foreground px-2 mb-1 tracking-wider">
                APPS
              </span>
            )}
            {items.map((item) => (
              <NavItem
                key={item.id}
                item={item}
                isActive={item.id === activeApp}
                activeSubId={activeSubNav[item.id]}
                collapsed={collapsed}
                onSelect={(id) => onSelect(id as AppId)}
                onSelectSub={onSelectSubNav}
              />
            ))}
          </div>

          <Separator />

          {/* Theme toggle */}
          <div className="px-2 py-2">
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={onThemeToggle}
                  className={cn(
                    'flex items-center gap-2 rounded-md w-full transition-colors text-muted-foreground hover:bg-accent hover:text-accent-foreground',
                    collapsed ? 'justify-center p-2' : 'px-2.5 py-2'
                  )}
                >
                  {theme === 'dark'
                    ? <Sun size={16} strokeWidth={1.5} />
                    : <Moon size={16} strokeWidth={1.5} />
                  }
                  {!collapsed && (
                    <span className="text-xs">
                      {theme === 'dark' ? 'Light mode' : 'Dark mode'}
                    </span>
                  )}
                </button>
              </TooltipTrigger>
              {collapsed && (
                <TooltipContent side="right">
                  {theme === 'dark' ? 'Light mode' : 'Dark mode'}
                </TooltipContent>
              )}
            </Tooltip>
          </div>

          {/* Fullscreen toggle */}
          <div className="px-2">
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={toggleFullscreen}
                  className={cn(
                    'flex items-center gap-2 rounded-md w-full transition-colors text-muted-foreground hover:bg-accent hover:text-accent-foreground',
                    collapsed ? 'justify-center p-2' : 'px-2.5 py-2'
                  )}
                >
                  {isFullscreen
                    ? <Minimize size={16} strokeWidth={1.5} />
                    : <Maximize size={16} strokeWidth={1.5} />
                  }
                  {!collapsed && (
                    <span className="text-xs">
                      {isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
                    </span>
                  )}
                </button>
              </TooltipTrigger>
              {collapsed && (
                <TooltipContent side="right">
                  {isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
                </TooltipContent>
              )}
            </Tooltip>
          </div>

          {/* User */}
          <div className="px-2 py-3">
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={onLogout}
                  className={cn(
                    'flex items-center gap-2 rounded-md w-full transition-colors text-muted-foreground hover:bg-accent hover:text-accent-foreground',
                    collapsed ? 'justify-center p-2' : 'px-2.5 py-2'
                  )}
                >
                  <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center text-[10px] font-semibold flex-shrink-0">
                    {initials}
                  </div>
                  {!collapsed && (
                    <div className="flex flex-col items-start min-w-0">
                      <span className="text-xs font-medium text-foreground truncate w-full">
                        {userName}
                      </span>
                      <span className="text-[10px] text-muted-foreground truncate w-full">
                        {userEmail}
                      </span>
                    </div>
                  )}
                </button>
              </TooltipTrigger>
              {collapsed && <TooltipContent side="right">Logout</TooltipContent>}
            </Tooltip>
          </div>
        </aside>

        {/* Edge hover toggle */}
        <div
          className="absolute top-0 bottom-0 right-0 w-3 translate-x-1/2 z-10 flex items-center justify-center cursor-pointer"
          onMouseEnter={() => setEdgeHovered(true)}
          onMouseLeave={() => setEdgeHovered(false)}
          onClick={onToggleCollapse}
        >
          <div
            className={cn(
              'flex items-center justify-center w-8 h-8 rounded-full border border-border bg-background text-muted-foreground transition-opacity duration-150',
              edgeHovered ? 'opacity-100' : 'opacity-0'
            )}
          >
            {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
          </div>
        </div>
      </div>

      {/* Create project dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Create project</DialogTitle>
            <DialogDescription>
              Add a new project to your workspace.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-3 py-2">
            <Label htmlFor="project-name">Project name</Label>
            <Input
              id="project-name"
              placeholder="e.g. GMT Fase 4"
              value={newProjectName}
              onChange={(e) => setNewProjectName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={!newProjectName.trim()}>Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </TooltipProvider>
  );
}